import { differenceInYears, format } from 'date-fns';
import { useFormik } from 'formik';
import _ from 'lodash';
import {
  ChangeEvent,
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import { ProfilePicture } from './components/ProfilePicture';

import {
  Button,
  Divider,
  Dropdown,
  Form,
  InputCheckbox,
  InputDatePicker,
  InputField,
  InputRadio,
  Loader,
  Modal,
} from '@/components';
import useAuth from '@/hooks/useAuth';
import { isBetWarriorEligible } from '@/hooks/useBetWarriorSegmentation';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { usePermutive } from '@/hooks/usePermutive';
import useToast from '@/hooks/useToast';
import useTshirtAndShield from '@/hooks/useTshirtAndShield';
import ROUTES from '@/router/routes';
import {
  getLocationParty,
  getPartyProvince,
  getSchoolsLocation,
} from '@/services';
import { saveTshirt } from '@/services/editAccount';
import { getProfileData, savePersonalData } from '@/services/editProfile';
import { sendEmailToChangePassword } from '@/services/editProfile_pwdReset';
import { defaultTshirtTeam, tshirtSponsor } from '@/store/tshirtAndShield';
import {
  Maybe,
  GENDER_ID,
  GenderValues,
  SelectValue,
  DocumentTypesValues,
  ARG_OR_EXT,
} from '@/types';
import { EditDataPost, SelectedScreen } from '@/types/editProfile';
import {
  createDateFromYear,
  datejs,
  DOC_TYPES,
  editPersonalDataSchema,
  editSchoolDataSchema,
  isArgOrExt,
  navigateBasePath403,
  OPTIONS_PROVINCES,
  parseObjectToSelectOption,
  parseToSelectOption,
  TEAM_LIST,
  TEXTS,
  validateFieldError,
  letterGenderById,
} from '@/utils';
import { SECTION_NAME_FOR_ADS } from '@/utils/ads';

type FormValues = {
  docType?: string;
  docNumber: string | number | null | undefined;
  name: string | undefined;
  lastName: string | undefined;
  email: string | undefined;
  birthDate: Maybe<Date>;
  supportedClub: Maybe<SelectValue>;
  gender: Maybe<GenderValues>;
  province: Maybe<SelectValue> | null;
  location: Maybe<SelectValue> | null;
  party: Maybe<SelectValue> | null;
  zipCode: string;
  street: string;
  streetNum: string;
  floor: string;
  phoneNum: string;
  areaCode: string;
  cellAreaCode: string;
  cellphoneNum: string;
  getNotifications?: boolean;
  customClubName: string;
  docArg: Maybe<DocumentTypesValues>;
  documentType: Maybe<SelectValue>;
  country: Maybe<SelectValue> | null;
  playTde: boolean;
  regProvince: Maybe<SelectValue> | null;
  regParty: Maybe<SelectValue> | null;
  regLocation: Maybe<SelectValue> | null;
  regSchool: Maybe<{
    label: string;
    value: number | string;
  }> | null;
  tdeType: 'primaria' | 'secundaria' | string | null;
};

interface TypeIdNombre {
  id: number;
  nombre: string;
}

const MyPersonalData = () => {
  const [manualDirty, setManualDirty] = useState(false);
  const queryClient = useQueryClient();
  const [listParty, setListParty] = useState<{
    global: SelectValue[];
    school: SelectValue[];
  }>({ global: [], school: [] });

  const [listLocation, setListLocation] = useState<{
    global: SelectValue[];
    school: SelectValue[];
  }>({ global: [], school: [] });
  const [listSchools, setListSchools] = useState<SelectValue[]>([]);
  const [tdeEligible, setTdeEligible] = useState<boolean>(false);
  const [tdeOptions, setTdeOptions] = useState<{
    primaria: boolean;
    secundaria: boolean;
  }>({
    primaria: false,
    secundaria: false,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAskEditTDE, setModalAskEditTDE] = useState(false);
  const [modalContent, setModalContent] = useState({
    title: '',
    render: () => <></>,
  });
  const [alreadyConfirmedSchool, setAlreadyConfirmedSchool] = useState(false);
  const toast = useToast();
  const { userInitialData, setUserInitialData } = useAuth();
  const { data: dataShieldTshirt, updateTshirtAndShield } =
    useTshirtAndShield();
  const { isMobile } = useIsDesktop();

  const navigate = useNavigate();
  const location = useLocation();
  const locationState: SelectedScreen = location.state;

  const schoolSectionRef = useRef<HTMLDivElement | null>(null);

  const { data: dataInit, isLoading } = useQuery(
    'initialDataProfile',
    async () => getProfileData(),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if ('codError' in data && 'mensaje' in data) {
          toast.show(data.mensaje as string);
          // navigate(ROUTES.basePath, { replace: true });
          navigateBasePath403(navigate);
        }
      },
      onError: (error) => {
        const err = error as Error;
        toast.showError(err);
        navigate(ROUTES.notFound, { replace: true });
      },
    }
  );

  const { trackPageView, isReady: isPermutiveReady } = usePermutive();

  useEffect(() => {
    // Permutive esta cargado
    if (isPermutiveReady) {
      trackPageView({
        section: SECTION_NAME_FOR_ADS.datosPersonales,
        kw_premium: userInitialData?.premium,
        kw_edad: userInitialData?.edad_dt,
        kw_sexo: letterGenderById(userInitialData?.idSexo),
        kw_equipo: userInitialData?.club_hincha,
        kw_fecha: userInitialData?.fechaActiva.orden,
        kw_pines: userInitialData?.pinesDisponibles,
        kw_saldo: userInitialData?.saldo,
        kw_puntosporfecha: userInitialData?.puntos_fecha,
        kw_puntosportorneo: userInitialData?.puntos_general,
        kw_posicionporfecha: userInitialData?.posicion_fecha,
        kw_posicionportorneo: userInitialData?.posicion_general,
        kw_recibirinfo:
          userInitialData?.recibeInformacion ||
          dataInit?.usuarioDT?.recibeInformacionJuego,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPermutiveReady]);

  useEffect(() => {
    if (location.state?.scrollTo && schoolSectionRef.current) {
      setTimeout(() => {
        schoolSectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 0);
    }
  }, [location]);

  const countriesList = useMemo(() => {
    if (dataInit && dataInit?.paises) {
      return dataInit.paises.map((country) => ({
        label: country.nombre,
        value: country.id,
      }));
    }

    return [];
  }, [dataInit]);

  const fetchGeography = async (
    id: number | null | undefined,
    fetchFunction: (id: number) => Promise<any>
  ) => {
    if (id) {
      const response = await fetchFunction(id);
      if (response) {
        return parseToSelectOption(response.geografia);
      }
    }
    return [];
  };

  // funcion para obtener el codigo de area y numero telefonico
  const extractFromNumber = (
    input: string | null | undefined,
    part: number
  ): string | undefined => {
    if (!input) {
      return undefined;
    }
    const parts = input.split('-');
    if (part === 0) {
      return parts[0];
    } else if (part === 1) {
      return parts[1];
    }
    return undefined;
  };
  useEffect(() => {
    const fetchData = async () => {
      if (dataInit?.usuarioDT?.provinciaId) {
        const response = await getPartyProvince(dataInit.usuarioDT.provinciaId);
        if (response) {
          const parties = parseToSelectOption(response.geografia);
          setListParty((prev) => ({ ...prev, global: parties }));

          // Buscar y establecer el partido inicial si existe
          if (dataInit?.usuarioDT?.partidoId) {
            const foundParty = parties.find(
              (p) => p.value === dataInit.usuarioDT?.partidoId
            );
            if (foundParty) {
              formik.setFieldValue('party', foundParty, false);
            }
          }
        }
      }

      if (dataInit?.usuarioDT?.partidoId) {
        const response = await getLocationParty(dataInit.usuarioDT.partidoId);
        if (response) {
          const locations = parseToSelectOption(response.geografia);
          setListLocation((prev) => ({ ...prev, global: locations }));

          // Buscar y establecer la localidad inicial si existe
          if (dataInit?.usuarioDT?.localidadId) {
            const foundLocation = locations.find(
              (l) => l.value === dataInit.usuarioDT?.localidadId
            );
            if (foundLocation) {
              formik.setFieldValue('location', foundLocation, false);
            }
          }
        }
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInit?.usuarioDT?.provinciaId, dataInit?.usuarioDT?.partidoId]);

  const initialValues = useMemo(() => {
    const country = dataInit?.usuarioDT?.idPais
      ? countriesList.find((c) => c.value === dataInit?.usuarioDT?.idPais) ??
        null
      : null;

    const normalizedCountry = country
      ? { label: country.label ?? '', value: country.value ?? 0 }
      : null;

    const docArg = dataInit?.usuarioDT?.dtExtranjero
      ? ARG_OR_EXT.ext
      : ARG_OR_EXT.arg;

    const documentType =
      DOC_TYPES.find(
        (doc) => doc.value === Number(dataInit?.usuarioDT?.tipoDocumentoId)
      ) ?? DOC_TYPES[0];

    let gender = null;
    if (Number(dataInit?.usuarioDT?.sexoId) === 1) {
      gender = GENDER_ID.hombre;
    } else if (Number(dataInit?.usuarioDT?.sexoId) === 2) {
      gender = GENDER_ID.mujer;
    }
    const birthDate = dataInit?.usuarioDT?.fechaNacimiento
      ? datejs(
          dataInit.usuarioDT.fechaNacimiento,
          'YYYY-MM-DD HH:mm:ss.SSS',
          'es'
        ).toDate()
      : null;

    let supportedClub: Maybe<SelectValue> = null;
    if (dataInit?.usuarioDT?.clubId) {
      supportedClub =
        TEAM_LIST.find((team) => team.value === dataInit?.usuarioDT?.clubId) ??
        null;
    } else {
      supportedClub = TEAM_LIST.find((team) => team.value === -1) ?? null;
    }

    let province: Maybe<SelectValue> = null;
    if (dataInit?.usuarioDT?.provinciaId) {
      province =
        OPTIONS_PROVINCES.find(
          (prov) => prov.value === dataInit?.usuarioDT?.provinciaId
        ) ?? null;
    }

    let party: Maybe<SelectValue> = null;
    if (dataInit?.usuarioDT?.partidoId && dataInit?.partidos) {
      const foundParty = dataInit.partidos.find(
        (partido) => partido.id === dataInit?.usuarioDT?.partidoId
      );
      if (foundParty) {
        party = {
          label: foundParty.nombre ?? '',
          value: foundParty.id ?? 0,
        };
      }
    }

    let location: Maybe<SelectValue> = null;
    if (dataInit?.usuarioDT?.localidadId && dataInit?.localidades) {
      const foundLocalidad = dataInit.localidades.find(
        (localidad) => localidad.id === dataInit?.usuarioDT?.localidadId
      );
      if (foundLocalidad) {
        location = {
          label: foundLocalidad.nombre ?? '',
          value: foundLocalidad.id ?? 0,
        };
      }
    }

    return {
      name: dataInit?.usuarioDT?.nombres ?? '',
      lastName: dataInit?.usuarioDT?.apellido ?? '',
      email: dataInit?.usuarioDT?.email ?? '',
      docType: dataInit?.usuarioDT?.tipoDocumentoId ?? '',
      docNumber: dataInit?.usuarioDT?.dtExtranjero
        ? dataInit?.usuarioDT?.nroDocumentoExtranjero
        : dataInit?.usuarioDT?.nroDocumento,
      zipCode: dataInit?.usuarioDT?.codigoPostal ?? '',
      street: dataInit?.usuarioDT?.calle ?? '',
      streetNum: dataInit?.usuarioDT?.numeroCalle ?? '',
      floor: dataInit?.usuarioDT?.piso ?? '',
      getNotifications: dataInit?.usuarioDT?.recibeInformacionJuego ?? false,
      areaCode: extractFromNumber(dataInit?.usuarioDT?.telefono, 0) || '',
      phoneNum: extractFromNumber(dataInit?.usuarioDT?.telefono, 1) || '',
      cellphoneNum: String(dataInit?.usuarioDT?.celular?.numero || ''),
      cellAreaCode: String(dataInit?.usuarioDT?.celular?.codigoArea || ''),
      customClubName: dataInit?.usuarioDT?.otroClub ?? '',
      country: normalizedCountry,
      documentType,
      docArg,
      gender,
      birthDate,
      supportedClub,
      province,
      party,
      location,
      playTde: !!dataInit?.escuelaId,
      regProvince: dataInit?.provinciaEscuela
        ? OPTIONS_PROVINCES.find(
            (prov) => prov.value === dataInit.provinciaEscuela
          ) ?? null
        : null,
      regParty: dataInit?.partidoEscuela
        ? parseObjectToSelectOption(
            dataInit?.partidosEscuela?.find(
              (p) => p.id === dataInit.partidoEscuela
            ) ?? {}
          )
        : null,
      regLocation: dataInit?.localidadEscuela
        ? parseObjectToSelectOption(
            dataInit?.localidadesEscuela?.find(
              (loc) => loc.id === dataInit.localidadEscuela
            ) ?? {}
          )
        : null,
      regSchool: dataInit?.escuelaId
        ? {
            value: dataInit.escuelaId.toString(),
            label: dataInit?.escuelaId.toString() || '',
          }
        : null,
      tdeType: dataInit?.tipoEscuelaId === 2 ? 'secundaria' : 'primaria',
    };
  }, [dataInit, countriesList]);

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues,
    validateOnBlur: true,
    validationSchema: editPersonalDataSchema,
    // solucion para comprar datos de la escuela
    isInitialValid: (values) => {
      const { initialValues: valuesToCompare } = { ...values } as {
        initialValues: FormValues;
      };

      if (!tdeEligible) {
        valuesToCompare.regProvince = initialValues.regProvince;
        valuesToCompare.regParty = initialValues.regParty;
        valuesToCompare.regLocation = initialValues.regLocation;
        valuesToCompare.regSchool = initialValues.regSchool;
        valuesToCompare.tdeType = initialValues.tdeType;
      }

      return _.isEqual(valuesToCompare, initialValues);
    },
    onSubmit: async (values, { setSubmitting }) => {
      // the validation is done here because isn't mandatory to do only when the user is editing school
      try {
        if (tdeEligible && !alreadyConfirmedSchool) {
          // Check if any school fields have been modified
          editSchoolDataSchema.validateSync({
            regLocation: values.regLocation,
            regParty: values.regParty,
            regProvince: values.regProvince,
            regSchool: values.regSchool?.value,
            tdeType: values.tdeType,
          });
        }
      } catch (error) {
        return setModalAskEditTDE(true);
      }

      setAlreadyConfirmedSchool(false);
      const formData = {
        apellido: values.lastName,
        nombres: values.name,
        provinciaId: values.province?.value,
        partidoId: values.party?.value,
        localidadId: values.location?.value,
        calle: values.street,
        numero: values.streetNum,
        piso: values.floor,
        codPostal: values.zipCode,
        tmp_area: values.areaCode ?? undefined,
        tmp_tel: values.phoneNum,
        nombreEquipo: dataInit?.usuarioDT?.nombreEquipo,
        fechaNacimiento: values.birthDate
          ? format(values.birthDate, 'dd/MM/yyyy')
          : undefined,
        hinchaId: values.supportedClub?.value,
        hinchaOtro: values.customClubName,
        sexo: values.gender?.toString(),
        celNumero: values.cellphoneNum
          ? Number(values.cellphoneNum)
          : undefined,
        celArea: values.cellAreaCode?.toString() ?? undefined,
        aceptaTransferir: values.getNotifications ? 'on' : '',
      } satisfies EditDataPost;

      try {
        const response = await savePersonalData(formData);
        await savePersonalData({
          updateEscuela: values.regSchool ? '1' : '',
          escuelaId:
            values.regSchool && tdeEligible
              ? Number(values.regSchool.value)
              : 0,
        });
        if (response) {
          if (response.estado) {
            toast.show(TEXTS.personalDataUpdated);
            if (response.usuarioDT && userInitialData) {
              const name = response.usuarioDT.nombres || '';
              const lastName = response.usuarioDT.apellido || '';
              const newPcia = values.province?.label || '';
              const newAge = values.birthDate
                ? differenceInYears(new Date(), values.birthDate)
                : userInitialData.edad_dt;
              setUserInitialData({
                ...userInitialData,
                club_hincha: values.supportedClub?.label || '',
                idSexo: String(values.gender),
                nombre_dt: `${name} ${lastName}`,
                recibeInformacion: values.getNotifications,
                provincia: newPcia,
                edad_dt: newAge,
              });

              // Si al cambiar la provincia (o la fecha de nacimiento) el
              // usuario deja de cumplir la segmentación de BetWarrior, la
              // camiseta guardada no puede seguir mostrando ese sponsor.
              const sigueElegible = isBetWarriorEligible({
                age: newAge,
                pcia: newPcia,
              });
              if (
                (!sigueElegible &&
                  dataShieldTshirt?.tshirt?.param5 ===
                    tshirtSponsor.betwarrior) ||
                (sigueElegible &&
                  dataShieldTshirt?.tshirt?.param5 === tshirtSponsor.clarin)
              ) {
                const tshirtSinSponsor = {
                  param1:
                    dataShieldTshirt.tshirt.param1 ?? defaultTshirtTeam.param1,
                  param2:
                    dataShieldTshirt.tshirt.param2 ?? defaultTshirtTeam.param2,
                  param3:
                    dataShieldTshirt.tshirt.param3 ?? defaultTshirtTeam.param3,
                  param4:
                    dataShieldTshirt.tshirt.param4 ?? defaultTshirtTeam.param4,
                  param5: tshirtSponsor.noSponsor,
                };
                // Persistimos en backend: si solo actualizamos el atom local,
                // el próximo fetch de getTshirtAndShield (p.ej. al entrar a
                // "Mi Equipo") pisa el cambio con el sponsor viejo guardado.
                await saveTshirt(tshirtSinSponsor);
                updateTshirtAndShield({ tshirt: tshirtSinSponsor });
                queryClient.invalidateQueries('initialDataTshitAndShield');
              }
            }
            queryClient.invalidateQueries('initialDataProfile');
            if (locationState && locationState.returnPage) {
              navigate(locationState.returnPage);
            }
          }
          toast.show(response.mensaje);
        }
      } catch (error) {
        toast.show(TEXTS.callCenterError);
      }
      setSubmitting(false);
    },
  });

  useEffect(() => {
    if (formik.values && formik.values.province === null) {
      setListParty((prev) => ({ ...prev, global: [] }));
      setListLocation((prev) => ({ ...prev, global: [] }));
    }
    if (formik.values && formik.values.party === null) {
      setListLocation((prev) => ({ ...prev, global: [] }));
    }
  }, [formik.values]);

  const {
    values,
    handleSubmit,
    isSubmitting,
    handleBlur,
    handleChange,
    setFieldValue,
    setFieldError,
    setTouched,
    setValues,
    errors,
    touched,
  } = formik;

  // Agrega este useEffect para comparar con los valores iniciales
  useEffect(() => {
    if (!isLoading && dataInit && initialValues && formik.values) {
      const initialValuesToCompare = initialValues;
      const currentValuesToCompare = { ...formik.values };

      // Si la sección de escuela no está activa, ignorar esos campos en la comparación
      if (!tdeEligible) {
        currentValuesToCompare.regProvince = initialValues.regProvince;
        currentValuesToCompare.regParty = initialValues.regParty;
        currentValuesToCompare.regLocation = initialValues.regLocation;
        currentValuesToCompare.regSchool = initialValues.regSchool;
        currentValuesToCompare.tdeType = initialValues.tdeType;
      }

      if (
        initialValues.regSchool?.label &&
        /^\d+$/.test(initialValues.regSchool.label) &&
        currentValuesToCompare.regSchool !== null
      ) {
        if (initialValuesToCompare.regSchool) {
          initialValuesToCompare.regSchool.label =
            currentValuesToCompare.regSchool?.label;
        }
      }

      const isActuallyDirty = !_.isEqual(
        currentValuesToCompare,
        initialValuesToCompare
      );
      setManualDirty(isActuallyDirty);
    }
  }, [
    formik.values,
    isLoading,
    dataInit,
    tdeEligible,
    initialValues,
    formik.dirty,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      if (values.regProvince?.value && values.regProvince?.value !== null) {
        const parties = await fetchGeography(
          values.regProvince.value,
          getPartyProvince
        );
        setListParty({ ...listParty, school: parties });
      }
      if (values.regParty?.value && values.regParty?.value !== null) {
        const locations = await fetchGeography(
          values.regParty.value,
          getLocationParty
        );
        setListLocation({ ...listLocation, school: locations });
      }
      if (values.regLocation?.value && values.regLocation?.value !== null) {
        const schools = await getSchoolsLocation(
          values.regLocation.value,
          values.tdeType === 'secundaria'
        );
        setListSchools(
          parseToSelectOption(
            schools.map((school) => ({
              id: Number(school.id),
              nombre: school.n,
            }))
          )
        );
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.regProvince, values.regParty, values.regLocation, values.tdeType]);

  const handleSelectProvince = async (e: SelectValue) => {
    await formik.setValues(
      {
        ...formik.values,
        province: e,
        party: null,
        location: null,
      },
      false
    ); // false para evitar validación

    if (e.value <= -1) return;

    const response = await getPartyProvince(e.value);

    if (response) {
      setListParty((prev) => ({
        ...prev,
        global: parseToSelectOption(response.geografia),
      }));
      setListLocation((prev) => ({ ...prev, global: [] }));
    }
  };

  const handleSelectParty = async (e: SelectValue) => {
    await formik.setValues(
      {
        ...formik.values,
        party: e,
        location: null,
      },
      false
    );

    if (e.value <= -1) return;

    const response = await getLocationParty(e.value);

    if (response) {
      setListLocation((prev) => ({
        ...prev,
        global: parseToSelectOption(response.geografia),
      }));
    }
  };

  const handleSelectLocation = async (e: SelectValue) => {
    await formik.setValues(
      {
        ...formik.values,
        location: e,
      },
      false
    );

    // eslint-disable-next-line no-useless-return
    if (e.value <= -1) return;
  };

  const handleOpenModal = async (type: 'email' | 'password') => {
    if (type === 'email') {
      setModalContent({
        title: 'Modificar e-mail',
        render: () => (
          <div className="flex flex-col my-4">
            <p className="text-center">{`Tu e-mail actual es ${dataInit?.emailAutenticado}. Para modificarlo, por favor comunicate con nuestro Centro de Ayuda al 0800-333-2343.`}</p>
            <Button
              className="mt-4"
              type="button"
              variant={'primary'}
              onClick={() => setIsModalOpen(false)}
            >
              Aceptar
            </Button>
          </div>
        ),
      });
    } else if (type === 'password') {
      const response = await sendEmailToChangePassword();
      if (!response.estado) {
        toast.show(response.mensaje);
        // No abrir el modal si hay un error
        return;
      } else {
        // response: "We've just sent you an email to reset your password."
        setModalContent({
          title: 'Modificar contraseña',
          render: () => (
            <div className="flex flex-col my-4">
              <p className="text-center">{`Se te envió un mail con las indicaciones para proceder al cambio de contraseña. Revisá tu casilla de e-mail`}</p>
              <Button
                className="mt-4"
                type="button"
                variant={'primary'}
                onClick={() => setIsModalOpen(false)}
              >
                Aceptar
              </Button>
            </div>
          ),
        });
      }
    }
    setIsModalOpen(true);
  };

  const getCalendarMaxDate = useMemo(() => {
    if (dataInit?.anioMaximoRegistracion) {
      return createDateFromYear(dataInit.anioMaximoRegistracion + 1)
        .subtract(1, 'day')
        .toDate();
    }

    return datejs().subtract(6, 'year').toDate();
  }, [dataInit]);

  const handleBlurGender = () => {
    if (values.gender === null) {
      setFieldError('gender', 'Requerido');
    }

    setTouched({ ...touched, gender: true });
  };

  const handleChangeNameValue = (name: string, value: string | number) => {
    setFieldValue(name, value);
  };

  const isArg = isArgOrExt(values.docArg);

  const handleStreetNumberChange = (
    event: ChangeEvent<HTMLInputElement>,
    onChange: any
  ) => {
    const { value } = event.target;
    const cleanedValue = value.replace(/[^0-9sn]/g, '');
    event.target.value = cleanedValue;
    onChange(event);
  };

  const registrationOptionsProvince = OPTIONS_PROVINCES;
  const registrationOptionsLocation = listLocation;

  const getTDEStatus = (birthDate: Date) => {
    const formatCutoffSecondary = `${dataInit?.anioCompSec}-${dataInit?.mesCompSec}-${dataInit?.diaCompSec}`;
    const secondaryCutoff = new Date(formatCutoffSecondary);

    const formatCutoffPrimary = `${dataInit?.anioComp}-${dataInit?.mesComp}-${dataInit?.diaComp}`;
    const earliestEligible = new Date(formatCutoffPrimary);

    if (birthDate >= secondaryCutoff && birthDate <= earliestEligible) {
      return { eligible: true, options: { primaria: true, secundaria: true } };
    } else if (birthDate >= secondaryCutoff && birthDate >= earliestEligible) {
      return { eligible: true, options: { primaria: true, secundaria: false } };
    }
    return { eligible: false, options: { primaria: false, secundaria: false } };
  };

  useEffect(() => {
    if (!values.birthDate) return;

    const { eligible, options } = getTDEStatus(values.birthDate);
    setTdeEligible(eligible);
    setTdeOptions(options);
    if (!eligible) {
      setFieldValue('tdeType', null);
      setFieldValue('regProvince', null);
      setFieldValue('regParty', null);
      setFieldValue('regLocation', null);
      setFieldValue('regSchool', null);
      return;
    }
    const newTdeType = options.secundaria ? 'secundaria' : 'primaria';

    if (values.tdeType !== newTdeType) {
      setFieldValue(
        'tdeType',
        dataInit?.tipoEscuelaId === 1 ? 'primaria' : newTdeType
      );
    }
  }, [values.birthDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fetchSchoolData = async () => {
      if (dataInit?.provinciaEscuela && dataInit?.escuelaId) {
        try {
          if (dataInit?.partidoEscuela) {
            setListParty({
              ...listParty,
              school: parseToSelectOption(
                dataInit?.partidosEscuela?.filter(
                  (item): item is TypeIdNombre => typeof item.id === 'number'
                ) || []
              ),
            });
          }

          if (dataInit?.localidadEscuela) {
            setListLocation({
              ...listParty,
              school: parseToSelectOption(
                dataInit?.localidadesEscuela?.filter(
                  (item): item is TypeIdNombre => typeof item.id === 'number'
                ) || []
              ),
            });
          }

          const schools = await getSchoolsLocation(
            dataInit?.localidadEscuela ?? 0,
            values.tdeType === 'secundaria'
          );

          setListSchools(
            parseToSelectOption(
              schools.map((s) => ({
                id: Number(s.id),
                nombre: s.n,
              }))
            )
          );

          const matchingSchool = schools.find(
            (s) => String(s.id) === String(dataInit.escuelaId)
          );

          if (matchingSchool) {
            setTimeout(() => {
              setFieldValue('regSchool', {
                value: String(dataInit.escuelaId),
                label: matchingSchool.n,
              });
            }, 0);
          }
        } catch (error) {
          console.error('Error fetching school data:', error);
        }
      }
    };

    fetchSchoolData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dataInit?.provinciaEscuela,
    dataInit?.escuelaId,
    dataInit?.localidadEscuela,
    values.tdeType,
  ]);

  return (
    // <EditMyAccountLayout
    //   goBackTo={ROUTES.editAccount.menu}
    //   title="Mis datos personales"
    // >
    <>
      {<Loader isVisible={isLoading || isSubmitting} timeout={isSubmitting} />}
      <div className="lg:ml-[115px] p-4 lg:p-0 lg:mt-10 lg:pr-[40px] lg:pb-14">
        {/* Foto de perfil */}
        <ProfilePicture />
        <Form.Form
          className="space-y-[1.75rem]"
          loading={isSubmitting}
          onSubmit={handleSubmit}
        >
          <div className="flex flex-col gap-y-4 lg:mx-0 lg:flex-row lg:gap-8 lg:gap-y-0">
            <Form.Field>
              <InputField
                onlyLetters
                behavior="default"
                error={validateFieldError(touched.name, errors.name)}
                label="Nombre"
                maxLength={30}
                name="name"
                placeholder="Nombre"
                value={values.name || ''}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Form.Field>
            <Form.Field>
              <InputField
                onlyLetters
                behavior="default"
                error={validateFieldError(touched.lastName, errors.lastName)}
                label="Apellido"
                maxLength={30}
                name="lastName"
                placeholder="Apellido"
                value={values.lastName || ''}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Form.Field>
          </div>
          <div className="flex flex-col gap-y-4  lg:flex-row lg:gap-8 lg:gap-y-0">
            <div className="flex flex-col gap-4 w-full">
              <Form.Field className="w-full">
                <InputField
                  onlyLetters
                  behavior="default"
                  disabled={true}
                  label="E-mail"
                  maxLength={30}
                  name="email"
                  placeholder="E-mail"
                  value={values.email || ''}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </Form.Field>
              <p
                className="font-bold text-gdt-blue underline cursor-pointer"
                onClick={() => handleOpenModal('email')}
              >
                Modificar e-mail
              </p>
            </div>
            <div className="flex flex-col gap-4 w-full">
              <Form.Field className="w-full">
                <InputField
                  onlyLetters
                  behavior="default"
                  disabled={true}
                  label="Contraseña"
                  maxLength={30}
                  name="name"
                  placeholder="Nombre"
                  value={'********'}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </Form.Field>
              <p
                className="font-bold text-gdt-blue underline cursor-pointer"
                onClick={() => handleOpenModal('password')}
              >
                Modificar Contraseña
              </p>
            </div>
          </div>
          <p className="font-light ">
            Chequeá tus datos con precisión. Con ellos te contactaremos en caso
            de que ganes premios.
          </p>
          <p className="text-lg ">Datos Generales</p>
          <div className="flex flex-col gap-y-4  lg:flex-row lg:gap-8 lg:gap-y-0">
            <Form.Field>
              <Dropdown
                disabled={true}
                label={isArg ? 'Tipo de Documento' : 'Nacionalidad'}
                options={(isArg ? DOC_TYPES : countriesList)
                  .filter(
                    (option) =>
                      option.label !== undefined && option.value !== undefined
                  ) // Filtra opciones válidas
                  .map((option) => ({
                    label: option.label as string, // Asegura que `label` sea un string
                    value: option.value as number, // Asegura que `value` sea un número
                  }))}
                selected={isArg ? values.documentType : values.country}
                onBlur={() => {
                  if (isArg) {
                    if (values.documentType === null) {
                      setFieldError('documentType', 'Requerido');
                    }
                    setTouched({ ...touched, documentType: true });
                  } else {
                    if (values.country === null) {
                      setFieldError('country', 'Requerido');
                    }
                    setTouched({ ...touched, country: true });
                  }
                }}
                onChange={(option) =>
                  setFieldValue(isArg ? 'documentType' : 'country', option)
                }
              />
            </Form.Field>
            <Form.Field>
              {!dataInit?.usuarioDT?.dtExtranjero && (
                <InputField
                  behavior="currency"
                  disabled={true}
                  label="N° de documento"
                  maskConfig={{
                    thousandSeparator: '.',
                  }}
                  maxLength={11}
                  name="docNumber"
                  placeholder="N° de documento"
                  value={values.docNumber?.toString() || ''}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              )}
              {dataInit?.usuarioDT?.dtExtranjero && (
                <InputField
                  behavior="default"
                  disabled={true}
                  label="N° de documento"
                  maxLength={11}
                  name="docNumber"
                  placeholder="N° de documento"
                  value={values.docNumber?.toString() || ''}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              )}
            </Form.Field>
          </div>
          <div className="flex flex-col gap-y-4  lg:flex-row lg:gap-8 lg:gap-y-0">
            <Form.Field>
              <InputDatePicker
                error={validateFieldError(touched.birthDate, errors.birthDate)}
                id="birthDate"
                label="Fecha de nacimiento"
                maxDate={getCalendarMaxDate}
                selected={values.birthDate}
                onBlur={handleBlur}
                onChange={(newVal) => {
                  setFieldValue('birthDate', newVal);
                }}
              />
            </Form.Field>
            <Form.Field>
              <InputRadio.Wrapper
                className="flex gap-4 lg:mt-[1.5rem!important]"
                error={validateFieldError(touched.gender, errors.gender)}
                label="Género"
              >
                <InputRadio.Input
                  isChecked={values.gender === GENDER_ID.hombre}
                  label="Hombre"
                  name="gender"
                  value={GENDER_ID.hombre}
                  onBlur={handleBlurGender}
                  onChange={handleChangeNameValue}
                />
                <InputRadio.Input
                  isChecked={values.gender === GENDER_ID.mujer}
                  label="Mujer"
                  name="gender"
                  value={GENDER_ID.mujer}
                  onBlur={handleBlurGender}
                  onChange={handleChangeNameValue}
                />
              </InputRadio.Wrapper>
            </Form.Field>
          </div>
          <div className="flex flex-col gap-y-4  lg:flex-row lg:gap-8 lg:gap-y-0">
            <Form.Field className="max-w-full lg:max-w-[50%]">
              <Dropdown
                error={validateFieldError(
                  touched.supportedClub,
                  errors.supportedClub
                )}
                label="Sos hincha de"
                options={TEAM_LIST}
                selected={values.supportedClub}
                onBlur={() => {
                  if (values.supportedClub === null) {
                    setFieldError('supportedClub', 'Requerido');
                  }

                  setTouched({ ...touched, supportedClub: true });
                }}
                onChange={(option) => {
                  setFieldValue('supportedClub', option);
                }}
              />
            </Form.Field>
            {(values.supportedClub?.value === -1 ||
              values.supportedClub === null) && (
              <Form.Field>
                <InputField
                  behavior="default"
                  error={validateFieldError(
                    touched.customClubName,
                    errors.customClubName,
                    values.customClubName.length
                  )}
                  label="Otro Club"
                  maxLength={30}
                  name="customClubName"
                  placeholder="Ingrese"
                  value={values.customClubName}
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </Form.Field>
            )}
          </div>
          <Divider className="my-4" type="horizontal" />
          <p className="text-lg ">Datos de Contacto</p>
          <div className="flex flex-col gap-y-4  lg:flex-row lg:gap-8 lg:gap-y-0">
            <Form.Field className="max-w-full lg:max-w-[50%]">
              <Dropdown
                disabled={dataInit?.usuarioDT?.dtExtranjero}
                error={validateFieldError(touched.province, errors.province)}
                label="Provincia"
                options={OPTIONS_PROVINCES}
                selected={values?.province}
                onChange={handleSelectProvince}
              />
            </Form.Field>
            <Form.Field className="max-w-full lg:max-w-[50%]">
              <Dropdown
                disabled={dataInit?.usuarioDT?.dtExtranjero}
                error={validateFieldError(touched.party, errors.party)}
                label="Partido"
                options={listParty.global}
                selected={values?.party}
                onChange={handleSelectParty}
              />
            </Form.Field>
          </div>
          <div className="flex flex-col gap-y-4  lg:flex-row lg:gap-8 lg:gap-y-0">
            <Form.Field className="max-w-full lg:max-w-[50%]">
              <Dropdown
                disabled={dataInit?.usuarioDT?.dtExtranjero}
                error={validateFieldError(touched.location, errors.location)}
                label="Localidad/Barrio"
                options={listLocation.global}
                selected={values?.location}
                onChange={handleSelectLocation}
              />
            </Form.Field>
            <Form.Field>
              <InputField
                behavior="default"
                error={validateFieldError(
                  touched.zipCode,
                  errors.zipCode,
                  values.zipCode.length
                )}
                label="Código Postal"
                maxLength={30}
                name="zipCode"
                placeholder="Código Postal"
                value={values.zipCode}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Form.Field>
          </div>
          <div className="flex flex-col gap-y-4  lg:flex-row lg:gap-8 lg:gap-y-0">
            <Form.Field>
              <InputField
                behavior="default"
                error={validateFieldError(
                  touched.street,
                  errors.street,
                  values.street.length
                )}
                label="Calle"
                maxLength={30}
                name="street"
                placeholder="Calle"
                value={values.street}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Form.Field>
            <Form.Field>
              <InputField
                behavior="default"
                error={validateFieldError(
                  touched.streetNum,
                  errors.streetNum,
                  values.streetNum.length
                )}
                label="N°"
                maxLength={30}
                name="streetNum"
                placeholder="N°"
                value={values.streetNum}
                onBlur={handleBlur}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleStreetNumberChange(event, handleChange)
                }
              />
            </Form.Field>
            <Form.Field>
              <InputField
                behavior="default"
                error={validateFieldError(
                  touched.floor,
                  errors.floor,
                  values.floor.length
                )}
                label="Piso/Depto"
                maxLength={30}
                name="floor"
                placeholder="Piso/Depto"
                value={values.floor}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Form.Field>
          </div>
          <div className="flex flex-col gap-y-4  lg:flex-row lg:gap-8 lg:gap-y-0">
            <div className="flex flex-col gap-2">
              <label className="text-sm text-gdt-gray font-bold">
                Telefono
              </label>
              <div className="flex gap-4">
                <Form.Field>
                  <InputField
                    behavior="default"
                    error={validateFieldError(
                      touched.areaCode,
                      errors.areaCode
                    )}
                    inputMode="numeric"
                    maxLength={4}
                    name="areaCode"
                    pattern="^[0-9]*$"
                    placeholder="Cód. área"
                    type="text"
                    value={values.areaCode}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Form.Field>
                <Form.Field>
                  <InputField
                    behavior="default"
                    error={validateFieldError(
                      touched.phoneNum,
                      errors.phoneNum
                    )}
                    inputMode="numeric"
                    maxLength={9}
                    name="phoneNum"
                    pattern="^[0-9]*$"
                    placeholder="Número"
                    type="text"
                    value={values.phoneNum}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Form.Field>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="text-sm text-gdt-gray font-bold">Celular</label>
              <div className="flex gap-4">
                <Form.Field>
                  <InputField
                    behavior="default"
                    error={validateFieldError(
                      touched.cellAreaCode,
                      errors.cellAreaCode
                    )}
                    inputMode="numeric"
                    maxLength={4}
                    name="cellAreaCode"
                    pattern="^[0-9]*$"
                    placeholder="Cód. área"
                    type="text"
                    value={values.cellAreaCode}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Form.Field>
                <Form.Field>
                  <InputField
                    behavior="default"
                    error={validateFieldError(
                      touched.cellphoneNum,
                      errors.cellphoneNum
                    )}
                    inputMode="numeric"
                    maxLength={30}
                    name="cellphoneNum"
                    pattern="^[0-9]*$"
                    placeholder="Número"
                    type="text"
                    value={values.cellphoneNum}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </Form.Field>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-y-4  lg:flex-row lg:gap-8 lg:gap-y-0">
            <InputCheckbox
              isChecked={!!values.getNotifications}
              label={
                <Fragment>
                  <span>
                    Acepto recibir notificaciones de terceros acerca de
                    consumos, promociones, productos y servicios. Ver{' '}
                    <Link
                      className="text-gdt-blue font-bold"
                      id="linkPolitics"
                      target="_blank"
                      to="/pdf/Reglamento_Gran_DT.pdf#page=23"
                    >
                      Política de Privacidad.
                    </Link>
                  </span>
                </Fragment>
              }
              name="getNotifications"
              onChange={(name, value) => setFieldValue(name, value)}
            />
          </div>
          <div
            ref={schoolSectionRef}
            className="mt-8 border-t pt-6 flex flex-col gap-y-4  lg:gap-8 lg:gap-y-0"
          >
            {/* Título y Subtítulo */}
            {tdeEligible ? (
              <>
                <p className="text-lg font-semibold mb-1">Tu escuela</p>
                <p className="text-sm mb-4">Seleccioná tu escuela:</p>
                {/* Radio Buttons para TDE */}
                <InputRadio.Wrapper
                  className="flex flex-row gap-4 lg:gap-8"
                  label=""
                >
                  {tdeOptions.primaria && (
                    <InputRadio.Input
                      isChecked={values.tdeType === 'primaria'}
                      label="Primaria"
                      name="tdeType"
                      value="primaria"
                      // onChange={() => setFieldValue('tdeType', 'primaria')}
                      onChange={() =>
                        setValues({
                          ...values,
                          tdeType: 'primaria',
                          regSchool: null,
                        })
                      }
                    />
                  )}
                  {tdeOptions.secundaria && (
                    <InputRadio.Input
                      isChecked={values.tdeType === 'secundaria'}
                      label="Secundaria"
                      name="tdeType"
                      value="secundaria"
                      // onChange={() => setFieldValue('tdeType', 'secundaria')}
                      onChange={() =>
                        setValues({
                          ...values,
                          tdeType: 'secundaria',
                          regSchool: null,
                        })
                      }
                    />
                  )}
                </InputRadio.Wrapper>
                {/* Campos de datos relacionados a la escuela */}
                <div className="flex flex-col gap-y-4 lg:flex-row lg:gap-8 mt-4">
                  <Form.Field className="lg:w-1/2">
                    <Dropdown
                      label="Provincia"
                      options={registrationOptionsProvince}
                      selected={values.regProvince}
                      onChange={(option) =>
                        setValues({
                          ...values,
                          regProvince: option,
                          regParty: null,
                          regLocation: null,
                          regSchool: null,
                        })
                      }
                    />
                  </Form.Field>
                  <Form.Field className="lg:w-1/2">
                    <Dropdown
                      label="Partido"
                      options={listParty.school}
                      selected={values.regParty}
                      onChange={(option) =>
                        setValues({
                          ...values,
                          regParty: option,
                          regLocation: null,
                          regSchool: null,
                        })
                      }
                    />
                  </Form.Field>
                </div>
                <div className="flex flex-col gap-y-4 lg:flex-row lg:gap-8 mt-4">
                  <Form.Field className="lg:w-1/2">
                    <Dropdown
                      label="Localidad/Barrio"
                      options={registrationOptionsLocation.school}
                      selected={values.regLocation}
                      onChange={(option) =>
                        setValues({
                          ...values,
                          regLocation: option,
                          regSchool: null,
                        })
                      }
                    />
                  </Form.Field>
                  <Form.Field className="lg:w-1/2">
                    <Dropdown
                      label="Escuela"
                      options={listSchools}
                      selected={values.regSchool as SelectValue}
                      onChange={(option) => setFieldValue('regSchool', option)}
                    />
                  </Form.Field>
                </div>
                <p className=" text-gray-600 mt-2">
                  Ante cualquier inconveniente para encontrar tu escuela, ponete
                  en contacto con nuestro Centro de Ayuda al 0800-333-2343
                </p>
              </>
            ) : null}
          </div>
          <div className="flex flex-col gap-y-4  lg:flex-row lg:gap-8 lg:gap-y-0">
            {isMobile ? (
              <div className="fixed bottom-0 left-0 right-0 bg-white p-4 z-50">
                <Button
                  className="w-full py-2 px-4"
                  disabled={!manualDirty || isSubmitting}
                  type="submit"
                  variant={'primary'}
                >
                  Guardar cambios
                </Button>
              </div>
            ) : (
              <Button
                className="mb-4 max-w-full lg:mt-12"
                disabled={!manualDirty || isSubmitting}
                type="submit"
                variant={'primary'}
              >
                Guardar cambios
              </Button>
            )}
          </div>
        </Form.Form>
      </div>
      <Modal
        closeOutside={false}
        controlledOnClose={() => setIsModalOpen(false)}
        isControlledOpen={isModalOpen}
        render={modalContent.render}
        showCloseButton={false}
        size="xs"
        title={modalContent.title}
        titleClassName="text-lg text-gdt-gray ml-5"
        variant="controlled"
      />
      <Modal
        closeOutside={false}
        controlledOnClose={() => setModalAskEditTDE(false)}
        isControlledOpen={modalAskEditTDE}
        render={() => (
          <div className="flex flex-col gap-y-4">
            <p className="text-center">
              Si completás los datos de tu escuela, participás automáticamente
              del Torneo de Escuelas. ¡Hay premios todas las fechas! ¿Querés
              hacerlo?
            </p>
            <div className="flex justify-center gap-5">
              <Button
                className=""
                type="button"
                variant={'primary'}
                onClick={() => {
                  setAlreadyConfirmedSchool(false);
                  setModalAskEditTDE(false);
                }}
              >
                Sí
              </Button>
              <Button
                className=""
                type="button"
                variant={'secondary'}
                onClick={() => {
                  setAlreadyConfirmedSchool(true);
                  setModalAskEditTDE(false);
                  handleSubmit();
                }}
              >
                No
              </Button>
            </div>
          </div>
        )}
        showCloseButton={false}
        size="xs"
        title={'Advertencia'}
        titleClassName=""
        variant="controlled"
      />
    </>
  );
};

export default MyPersonalData;
